import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
  Square,
  Circle,
} from "@chakra-ui/react";
import {
  AiOutlineTags,
  AiOutlineLock,
  AiOutlineSearch,
  AiOutlinePlus,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineWarning,
} from "react-icons/ai";
import api from "../../services/api";
import { getIsAdminFromRecord } from "../../components/FullCalendar/FullCalendarUtils";
import { isSprintCategory } from "../../components/FullCalendar/FullCalendarUtils";

const PRESET_COLORS = [
  "#02ea75", // Green
  "#3182ce", // Blue
  "#ecc94b", // Yellow
  "#e53e3e", // Red
  "#805ad5", // Purple
  "#d69e2e", // Orange
  "#38a169", // Dark Green
  "#d53f8c", // Pink
  "#0bc5ea", // Cyan
  "#fefcbf", // Light Yellow
];

const FORM_INICIAL_CATEGORIA = { id: null, name: "", color: "#02ea75", active: true, consumesCycle: false };

export default function CategoriasPage() {
  const toast = useToast();

  const [me, setMe] = useState(null);
  const [ready, setReady] = useState(false);
  const isAdmin = getIsAdminFromRecord(me);

  const [categorias, setCategorias] = useState([]);
  const [buscaCategoria, setBuscaCategoria] = useState("");

  const { isOpen: isCatOpen, onOpen: onCatOpen, onClose: onCatClose } = useDisclosure();
  const [formCat, setFormCat] = useState(FORM_INICIAL_CATEGORIA);
  const [editandoCat, setEditandoCat] = useState(false);
  const [savingCat, setSavingCat] = useState(false);

  const { isOpen: isCatDelOpen, onOpen: onCatDelOpen, onClose: onCatDelClose } = useDisclosure();
  const [catExcluindo, setCatExcluindo] = useState(null);
  const [deletingCat, setDeletingCat] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get("/calendar/categories");
      // Sprint é categoria de sistema (fluxo Criar Sprint) e não é gerenciada aqui
      const lista = Array.isArray(res.data) ? res.data.filter((c) => !isSprintCategory(c.name)) : [];
      setCategorias(lista);
    } catch (err) {
      console.error(err);
      toast({ title: "Erro ao carregar categorias", status: "error", duration: 3000 });
    }
  }, [toast]);

  // Descobre o usuário logado e busca categorias.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/calendar/me");
        setMe(res.data || null);
        if (getIsAdminFromRecord(res.data)) {
          await loadCategories();
        }
      } catch {
        setMe(null);
      } finally {
        setReady(true);
      }
    })();
  }, [loadCategories]);

  // Filter Categorias
  const categoriasFiltradas = useMemo(() => {
    if (!buscaCategoria.trim()) return categorias;
    const q = buscaCategoria.toLowerCase();
    return categorias.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [categorias, buscaCategoria]);

  // Categories Handlers
  const openCreateCat = () => {
    setEditandoCat(false);
    setFormCat(FORM_INICIAL_CATEGORIA);
    onCatOpen();
  };

  const openEditCat = (cat) => {
    setEditandoCat(true);
    setFormCat({
      id: cat.id,
      name: cat.name || "",
      color: cat.color || "#02ea75",
      active: cat.active ?? true,
      consumesCycle: cat.consumesCycle ?? false,
    });
    onCatOpen();
  };

  const handleSalvarCat = async () => {
    if (!formCat.name.trim()) {
      toast({ title: "O nome da categoria é obrigatório", status: "warning", duration: 2000 });
      return;
    }
    setSavingCat(true);

    try {
      const payload = {
        name: formCat.name.trim(),
        color: formCat.color,
        consumesCycle: !!formCat.consumesCycle,
        active: !!formCat.active,
      };

      if (editandoCat) {
        await api.put(`/calendar/categories/${formCat.id}`, payload);
        toast({ title: "Categoria atualizada com sucesso", status: "success", duration: 2000 });
      } else {
        await api.post("/calendar/categories", payload);
        toast({ title: "Categoria criada com sucesso", status: "success", duration: 2000 });
      }
      await loadCategories();
      onCatClose();
    } catch (err) {
      console.error(err);
      toast({
        title: "Erro ao salvar categoria",
        description: err?.response?.data?.message || err?.message || "Tente novamente.",
        status: "error",
        duration: 3000,
      });
    } finally {
      setSavingCat(false);
    }
  };

  const openDeleteCat = (cat) => {
    setCatExcluindo(cat);
    onCatDelOpen();
  };

  const handleConfirmarExcluirCat = async () => {
    if (!catExcluindo) return;
    setDeletingCat(true);

    try {
      await api.delete(`/calendar/categories/${catExcluindo.id}`);
      toast({ title: "Categoria removida com sucesso", status: "info", duration: 2000 });
      await loadCategories();
      onCatDelClose();
      setCatExcluindo(null);
    } catch (err) {
      console.error(err);
      toast({
        title: "Erro ao remover categoria",
        description: err?.response?.data?.message || err?.message || "Tente novamente.",
        status: "error",
        duration: 3000,
      });
    } finally {
      setDeletingCat(false);
    }
  };

  // Gate: tela só para admins.
  if (!ready) {
    return (
      <Flex justify="center" py={20}>
        <Spinner size="lg" color="secondary" />
      </Flex>
    );
  }

  if (!isAdmin) {
    return (
      <VStack spacing={3} py={20} align="center" color="textSecondary">
        <Icon as={AiOutlineLock} boxSize="32px" color="textMuted" />
        <Text fontWeight="800" color="textPrimary">Acesso restrito</Text>
        <Text fontSize="sm" textAlign="center" maxW="420px">
          Esta tela é exclusiva para administradores.
        </Text>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={4}
        w="100%"
      >
        <Heading size="lg" color="textPrimary" fontWeight="900">
          <HStack spacing={3}>
            <Icon as={AiOutlineTags} color="secondary" />
            <Text>Categorias</Text>
          </HStack>
        </Heading>
        {/* Busca e botão dividem a mesma linha em qualquer largura */}
        <HStack spacing={3} flexWrap="nowrap">
          <InputGroup flex="1" maxW={{ md: "260px" }} minW="0" size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={AiOutlineSearch} color="textSecondary" />
            </InputLeftElement>
            <Input
              placeholder="Buscar categoria..."
              value={buscaCategoria}
              onChange={(e) => setBuscaCategoria(e.target.value)}
              autoComplete="off"
              bg="surface"
              border="1px solid"
              borderColor="borderPrimary"
              _focus={{ borderColor: "secondary", boxShadow: "none" }}
              color="textPrimary"
              borderRadius="xl"
            />
          </InputGroup>
          <Button
            leftIcon={<AiOutlinePlus />}
            size="sm"
            colorScheme="green"
            fontWeight="800"
            flexShrink={0}
            onClick={openCreateCat}
          >
            Nova Categoria
          </Button>
        </HStack>
      </Flex>

      {/* Tabela de Categorias */}
      <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
        <Flex align="center" gap={3} p={4} bg="surfaceRaised" borderBottom="1px solid" borderColor="borderPrimary">
          <Icon as={AiOutlineTags} color="secondary" boxSize="20px" />
          <Heading size="sm" color="textPrimary" textTransform="uppercase" letterSpacing="1.5px" fontWeight="800">
            Categorias de Período OFF
          </Heading>
          <Badge ml="auto" bg="rgba(2,234,117,0.15)" color="secondary" fontWeight="800">
            {categoriasFiltradas.length}
          </Badge>
        </Flex>

        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr bg="surfaceRaised">
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px">Nome</Th>
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px">Cor Associada</Th>
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px">Consome Férias</Th>
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px">Status</Th>
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px" isNumeric>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {categoriasFiltradas.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={10} color="textSecondary" borderColor="borderPrimary">
                    Nenhuma categoria encontrada
                  </Td>
                </Tr>
              ) : (
                categoriasFiltradas.map((cat) => (
                  <Tr key={cat.id} _hover={{ bg: "surfaceHover" }}>
                    <Td borderColor="borderPrimary" py={3}>
                      <Text fontWeight="800" fontSize="sm" color="textPrimary">
                        {cat.name}
                      </Text>
                    </Td>
                    <Td borderColor="borderPrimary" py={3}>
                      <HStack spacing={2}>
                        <Circle size="14px" bg={cat.color} border="1px solid rgba(255,255,255,0.2)" />
                        <Text fontSize="xs" fontFamily="mono" color="textSecondary">
                          {cat.color.toUpperCase()}
                        </Text>
                      </HStack>
                    </Td>
                    <Td borderColor="borderPrimary" py={3}>
                      <Badge
                        bg={cat.consumesCycle ? "rgba(2,234,117,0.15)" : "surfaceRaised"}
                        color={cat.consumesCycle ? "#02EA75" : "textSecondary"}
                        fontSize="10px"
                        fontWeight="700"
                        borderRadius="md"
                        px={2}
                        py={0.5}
                      >
                        {cat.consumesCycle ? "Sim" : "Não"}
                      </Badge>
                    </Td>
                    <Td borderColor="borderPrimary" py={3}>
                      <Badge
                        bg={cat.active ? "rgba(2,234,117,0.15)" : "rgba(229,62,62,0.12)"}
                        color={cat.active ? "#02EA75" : "#FC8181"}
                        fontSize="10px"
                        fontWeight="700"
                        borderRadius="md"
                        px={2}
                        py={0.5}
                      >
                        {cat.active ? "Ativo" : "Inativo"}
                      </Badge>
                    </Td>
                    <Td borderColor="borderPrimary" py={3} isNumeric>
                      <HStack spacing={1} justify="flex-end">
                        <IconButton
                          aria-label="Editar"
                          icon={<AiOutlineEdit />}
                          size="xs"
                          variant="ghost"
                          color="textSecondary"
                          _hover={{ color: "secondary", bg: "surfaceHover" }}
                          onClick={() => openEditCat(cat)}
                        />
                        <IconButton
                          aria-label="Excluir"
                          icon={<AiOutlineDelete />}
                          size="xs"
                          variant="ghost"
                          color="textSecondary"
                          _hover={{ color: "red.400", bg: "rgba(229,62,62,0.1)" }}
                          onClick={() => openDeleteCat(cat)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Modal: Criar/Editar Categoria */}
      <Modal isOpen={isCatOpen} onClose={onCatClose} isCentered size="lg">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl">
          <ModalHeader borderBottom="1px solid" borderColor="borderPrimary">
            <HStack spacing={3}>
              <Icon as={AiOutlineTags} color="secondary" boxSize="20px" />
              <Text>{editandoCat ? "Editar Categoria" : "Nova Categoria"}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="textSecondary" />
          <ModalBody py={5}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                  Nome da Categoria
                </FormLabel>
                <Input
                  value={formCat.name}
                  onChange={(e) => setFormCat((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Ex: Férias, Evento, Reunião"
                  bg="surfaceRaised"
                  borderColor="borderPrimary"
                  color="textPrimary"
                  _placeholder={{ color: "textMuted" }}
                  _focus={{ borderColor: "secondary", boxShadow: "none" }}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                  Cor da Categoria
                </FormLabel>
                <VStack align="stretch" spacing={3}>
                  <HStack spacing={2} wrap="wrap">
                    {PRESET_COLORS.map((c) => (
                      <Circle
                        key={c}
                        size="32px"
                        bg={c}
                        cursor="pointer"
                        border={formCat.color.toLowerCase() === c.toLowerCase() ? "2px solid var(--chakra-colors-textPrimary)" : "2px solid transparent"}
                        boxShadow={formCat.color.toLowerCase() === c.toLowerCase() ? "0 0 10px rgba(255,255,255,0.6)" : "none"}
                        _hover={{ transform: "scale(1.1)" }}
                        transition="all 0.15s ease"
                        onClick={() => setFormCat((p) => ({ ...p, color: c }))}
                      />
                    ))}
                  </HStack>
                  <HStack spacing={3}>
                    <Input
                      value={formCat.color}
                      onChange={(e) => setFormCat((p) => ({ ...p, color: e.target.value }))}
                      placeholder="#hexadecimal"
                      maxW="140px"
                      bg="surfaceRaised"
                      borderColor="borderPrimary"
                      color="textPrimary"
                      _focus={{ borderColor: "secondary", boxShadow: "none" }}
                    />
                    <Square size="36px" bg={formCat.color} borderRadius="md" border="1px solid rgba(255,255,255,0.2)" />
                  </HStack>
                </VStack>
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                    Consome limite de Férias?
                  </FormLabel>
                  <Select
                    value={formCat.consumesCycle ? "true" : "false"}
                    onChange={(e) => setFormCat((p) => ({ ...p, consumesCycle: e.target.value === "true" }))}
                    bg="surfaceRaised"
                    borderColor="borderPrimary"
                    color="textPrimary"
                    _focus={{ borderColor: "secondary", boxShadow: "none" }}
                    sx={{ "& > option": { background: "var(--chakra-colors-surface)", color: "var(--chakra-colors-textPrimary)" } }}
                  >
                    <option value="false">Não</option>
                    <option value="true">Sim</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                    Status
                  </FormLabel>
                  <Select
                    value={formCat.active ? "true" : "false"}
                    onChange={(e) => setFormCat((p) => ({ ...p, active: e.target.value === "true" }))}
                    bg="surfaceRaised"
                    borderColor="borderPrimary"
                    color="textPrimary"
                    _focus={{ borderColor: "secondary", boxShadow: "none" }}
                    sx={{ "& > option": { background: "var(--chakra-colors-surface)", color: "var(--chakra-colors-textPrimary)" } }}
                  >
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </Select>
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
            <Button variant="ghost" color="textSecondary" _hover={{ bg: "surfaceHover" }} onClick={onCatClose} size="sm" isDisabled={savingCat}>
              Cancelar
            </Button>
            <Button colorScheme="green" fontWeight="800" onClick={handleSalvarCat} size="sm" isLoading={savingCat}>
              {editandoCat ? "Salvar" : "Criar Categoria"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal: Confirmar Exclusão Categoria */}
      <Modal isOpen={isCatDelOpen} onClose={onCatDelClose} isCentered size="md">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl">
          <ModalHeader borderBottom="1px solid" borderColor="borderPrimary">
            <HStack spacing={3}>
              <Icon as={AiOutlineWarning} color="#FC8181" boxSize="20px" />
              <Text>Excluir categoria</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="textSecondary" />
          <ModalBody py={5}>
            <Text fontSize="sm" color="textSecondary">
              Tem certeza que deseja excluir a categoria <strong>{catExcluindo?.name}</strong>?
              Os eventos que já utilizam essa categoria poderão perder a cor associada.
            </Text>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
            <Button variant="ghost" color="textSecondary" _hover={{ bg: "surfaceHover" }} onClick={onCatDelClose} size="sm" isDisabled={deletingCat}>
              Cancelar
            </Button>
            <Button colorScheme="red" fontWeight="800" onClick={handleConfirmarExcluirCat} size="sm" isLoading={deletingCat}>
              Excluir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
